import { z } from "zod";

/**
 * WhatsApp closed-window send flow types + schemas.
 * Backend contract: plans/WHATSAPP_CLOSED_WINDOW_FLOW_FRONTEND_PLAN.md
 */

export type WhatsAppTemplateStatus =
  | "DRAFT"
  | "APPROVED"
  | "PENDING"
  | "REJECTED"
  | "PAUSED"
  | "DISABLED"
  | "DEPRECATED";

export type WhatsAppTemplateCategory =
  | "MARKETING"
  | "UTILITY"
  | "AUTHENTICATION";

export type WhatsAppLifecycleEvent =
  | "PRE_CHECKIN_REMINDER"
  | "CHECKIN_DAY_GREETING"
  | "ROOM_READY"
  | "STAY_MIDPOINT_CHECKIN"
  | "PRE_CHECKOUT_REMINDER"
  | "CHECKOUT_FEEDBACK_REQUEST"
  | "REVIEW_REQUEST"
  | "INVOICE_READY"
  | "BOOKING_CONFIRMED"
  | "BOOKING_CANCELED"
  | "CUSTOM";

export interface WhatsAppTemplateRecommendedBinding {
  event: WhatsAppLifecycleEvent;
  language: string;
  triggerOffsetMinutes?: number | null;
  variableSources: Record<string, string>;
}

export interface WhatsAppTemplateMetadata {
  reengagement?: boolean;
  systemTemplate?: boolean;
  lifecycleEvent?: WhatsAppLifecycleEvent;
  blueprintName?: string;
  recommendedBinding?: WhatsAppTemplateRecommendedBinding;
}

export interface WhatsAppTemplate {
  id: string;
  hotelId?: string;
  name: string;
  language: string;
  metaTemplateId?: string | null;
  category: WhatsAppTemplateCategory | string;
  status: WhatsAppTemplateStatus;
  variables: string[];
  hasQuickReplyButton: boolean;
  metadata: WhatsAppTemplateMetadata | null;
  components: unknown;
  statusReason?: string | null;
  lastSyncedAt?: string | null;
}

export interface WhatsAppTemplateBlueprint {
  event: WhatsAppLifecycleEvent;
  name: string;
  language: string;
  category: WhatsAppTemplateCategory;
  description: string;
  components: Record<string, unknown>[];
  recommendedBinding: WhatsAppTemplateRecommendedBinding;
  reengagement: boolean;
}

export interface WhatsAppTemplateBinding {
  id: string;
  hotelId: string;
  event: WhatsAppLifecycleEvent;
  language: string;
  templateId: string;
  enabled: boolean;
  triggerOffsetMinutes: number | null;
  variableSources: Record<string, string>;
  createdAt: string;
  updatedAt: string;
}

export type WhatsAppTemplateBootstrapAction =
  | "CREATED_DRAFT"
  | "REUSED_EXISTING"
  | "SUBMITTED_TO_META"
  | "WAITING_FOR_META_APPROVAL"
  | "BOUND"
  | "SKIPPED_UNSUPPORTED_STATUS";

export interface WhatsAppTemplateSyncResult {
  upserted: number;
  deprecated: number;
  unchanged: number;
}

export interface WhatsAppTemplateBootstrapItem {
  event: WhatsAppLifecycleEvent;
  name: string;
  language: string;
  templateId?: string;
  metaTemplateId?: string | null;
  status?: WhatsAppTemplateStatus;
  bindingId?: string;
  actions: WhatsAppTemplateBootstrapAction[];
  error?: string;
}

export interface WhatsAppTemplateBootstrapResult {
  language: string;
  sync?: WhatsAppTemplateSyncResult;
  syncError?: string;
  total: number;
  createdDrafts: number;
  submitted: number;
  bound: number;
  pending: number;
  approved: number;
  skipped: number;
  failed: number;
  items: WhatsAppTemplateBootstrapItem[];
}

export interface WhatsAppBusinessHoursWindow {
  day: "MON" | "TUE" | "WED" | "THU" | "FRI" | "SAT" | "SUN";
  from: string;
  to: string;
}

export interface WhatsAppBusinessHours {
  tz: string;
  windows: WhatsAppBusinessHoursWindow[];
}

export interface WhatsAppHotelConfig {
  id: string;
  hotelId: string;
  enabled: boolean;
  defaultLanguage: string;
  liveAutoReplyEnabled: boolean;
  liveHumanHandoffEnabled: boolean;
  systemTemplatesEnabled: boolean;
  reengagementEnabled: boolean;
  businessHours: WhatsAppBusinessHours | null;
  outOfHoursTemplateId: string | null;
  phoneNumberIdCache: string | null;
  wabaIdCache: string | null;
  preCheckinFlowId: string | null;
  preCheckinFlowName: string | null;
  preCheckinFlowStatus: string | null;
  preCheckoutFlowId: string | null;
  preCheckoutFlowName: string | null;
  preCheckoutFlowStatus: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateWhatsAppHotelConfigRequest {
  enabled?: boolean;
  defaultLanguage?: string;
  liveAutoReplyEnabled?: boolean;
  liveHumanHandoffEnabled?: boolean;
  systemTemplatesEnabled?: boolean;
  reengagementEnabled?: boolean;
  businessHours?: WhatsAppBusinessHours | null;
  outOfHoursTemplateId?: string | null;
}

export type WhatsAppMessageDirection = "INBOUND" | "OUTBOUND";

export type WhatsAppMessageStatus =
  | "PENDING"
  | "SENT"
  | "DELIVERED"
  | "READ"
  | "FAILED";

export interface WhatsAppMessage {
  id: string;
  conversationId: string;
  direction: WhatsAppMessageDirection;
  type: string;
  status: WhatsAppMessageStatus;
  templateId: string | null;
  bodyText: string | null;
  errorCode: string | null;
  errorMessage: string | null;
  occurredAt: string;
  sentAt: string | null;
  failedAt: string | null;
}

export interface WhatsAppConversation {
  id: string;
  hotelId: string;
  /**
   * Window is open when `windowOpenUntil != null && new Date(windowOpenUntil) > new Date()`.
   * Reopens only on an inbound guest message / Quick-Reply tap — never on an outbound send.
   */
  windowOpenUntil: string | null;
  /** Booking the conversation belongs to, used to resolve it from the chat. */
  bookingId: string | null;
  /** Stream channel currently bound to this conversation — the bridge from the chat composer. */
  currentStreamChannelId: string | null;
}

/**
 * Bare reason tokens the backend returns in `message` on a `409` send refusal.
 * Branch on these by exact match — never substring-parse.
 */
export type WhatsAppSendRefusal =
  | "WHATSAPP_NO_CONVERSATION"
  | "WINDOW_CLOSED"
  | "WHATSAPP_DISABLED"
  | "TEMPLATE_NOT_APPROVED"
  | "WHATSAPP_TEMPLATE_NOT_FOUND"
  | "REENGAGEMENT_DISABLED"
  | "KILL_SWITCH"
  | "WHATSAPP_UNSUPPORTED_MEDIA_TYPE"
  | "WHATSAPP_RE_ENGAGEMENT_REQUIRED"
  | "WHATSAPP_RECIPIENT_NOT_OPTED_IN";

export const WHATSAPP_SEND_REFUSALS: readonly WhatsAppSendRefusal[] = [
  "WHATSAPP_NO_CONVERSATION",
  "WINDOW_CLOSED",
  "WHATSAPP_DISABLED",
  "TEMPLATE_NOT_APPROVED",
  "WHATSAPP_TEMPLATE_NOT_FOUND",
  "REENGAGEMENT_DISABLED",
  "KILL_SWITCH",
  "WHATSAPP_UNSUPPORTED_MEDIA_TYPE",
  "WHATSAPP_RE_ENGAGEMENT_REQUIRED",
  "WHATSAPP_RECIPIENT_NOT_OPTED_IN",
] as const;

/**
 * The subset of `409` tokens that means the agent can recover in-chat by sending an
 * approved re-engagement template — i.e. open the template picker. Every other `409`
 * (disabled / kill switch / template-not-approved) is a dead-end inline error.
 */
export type WhatsAppReengagementToken = Extract<
  WhatsAppSendRefusal,
  | "WHATSAPP_NO_CONVERSATION"
  | "WINDOW_CLOSED"
  | "WHATSAPP_RE_ENGAGEMENT_REQUIRED"
  | "WHATSAPP_RECIPIENT_NOT_OPTED_IN"
>;

export const WHATSAPP_REENGAGEMENT_TOKENS: readonly WhatsAppReengagementToken[] =
  [
    "WHATSAPP_NO_CONVERSATION",
    "WINDOW_CLOSED",
    "WHATSAPP_RE_ENGAGEMENT_REQUIRED",
    "WHATSAPP_RECIPIENT_NOT_OPTED_IN",
  ] as const;

export const isReengagementToken = (
  token: string,
): token is WhatsAppReengagementToken =>
  (WHATSAPP_REENGAGEMENT_TOKENS as readonly string[]).includes(token);

/**
 * Classification of a WhatsApp send failure, derived from the HTTP status (which
 * carries the intent) and the bare error token:
 * - `reengage` (`409` + a re-engagement token) → open the template picker.
 * - `refusal` (any other `409`) → inline/toast error, no picker.
 * - `upstream` (`502`) → the integration itself failed; message never left the
 *   building, a template won't help. `WHATSAPP_AUTH_FAILED` means an admin must
 *   refresh the Meta access token.
 * - `unknown` → anything else; generic error.
 */
export type WhatsAppSendErrorKind =
  | "reengage"
  | "refusal"
  | "upstream"
  | "unknown";

export interface ClassifiedWhatsAppSendError {
  kind: WhatsAppSendErrorKind;
  token: string;
  status: number;
}

export const sendTextSchema = z.object({
  text: z.string().min(1).max(4096),
});
export type SendTextPayload = z.infer<typeof sendTextSchema>;

export const sendTemplateSchema = z.object({
  templateId: z.uuid(),
  variables: z.record(z.string(), z.string()),
});
export type SendTemplatePayload = z.infer<typeof sendTemplateSchema>;

/**
 * A conversation's customer-service window is open when there is a future
 * `windowOpenUntil`. Pure helper so the composer and host screen agree.
 */
export const isWindowOpen = (
  windowOpenUntil: string | null | undefined,
): boolean => windowOpenUntil != null && new Date(windowOpenUntil) > new Date();

/**
 * Re-engagement-eligible template: APPROVED and able to reopen the window
 * (Quick Reply button or explicit re-engagement metadata).
 */
export const isReengagementEligible = (template: WhatsAppTemplate): boolean =>
  template.status === "APPROVED" &&
  (template.hasQuickReplyButton || template.metadata?.reengagement === true);
